import { type EntityState } from '@ngrx/entity';

import {
  type CanvasElement,
  type CanvasElementId,
  type ElementTargetPath,
  type SlotId,
} from '@blaze-runtime/engine-domain';

import { hasSlots } from '../../canvas-elements/element-model/element-props-guards';
import { getElementPath } from '../canvas-element/path';

// TODO: test
export function getPathFirstInSlot(
  entityState: EntityState<CanvasElement>,
  elementId: CanvasElementId,
  slotId: SlotId,
): ElementTargetPath {
  return getPathInSlotAtIndex(entityState, elementId, slotId, 0);
}

/**
 * A placement between the slot's existing children, at `index`. Unlike a 'top'/'bottom'
 * target, this adds no breakLine separators around the new element - it simply joins the
 * children already in the slot.
 */
export function getPathInSlotAtIndex(
  entityState: EntityState<CanvasElement>,
  elementId: CanvasElementId,
  slotId: SlotId,
  index: number,
): ElementTargetPath {
  const element = entityState.entities[elementId];
  if (!hasSlots(element)) {
    throw new Error('Could not find element with slots');
  }

  const slot = element.slots[slotId];
  if (!slot) {
    throw new Error('Could not find slot');
  }

  return {
    id: elementId,
    parentId: elementId,
    slot: slotId,
    index,
    targetPosition: { type: 'left' },
  };
}

export function getPathBeforeElement(
  entityState: EntityState<CanvasElement>,
  elementId: CanvasElementId,
): ElementTargetPath {
  const targetElementPath = getElementPath(entityState.entities, elementId);
  if (!targetElementPath || targetElementPath.index === undefined) {
    throw new Error(`Could not find path for element ${elementId}`);
  }

  // Target before (left) the element
  targetElementPath.targetPosition = { type: 'left' };

  return targetElementPath;
}

export function getPathBelowElement(
  entityState: EntityState<CanvasElement>,
  elementId: CanvasElementId,
): ElementTargetPath {
  const targetElementPath = getElementPath(entityState.entities, elementId);
  if (!targetElementPath || targetElementPath.index === undefined) {
    throw new Error(`Could not find path for element ${elementId}`);
  }

  // Target below the element
  targetElementPath.index += 1;
  targetElementPath.targetPosition = { type: 'bottom' };

  return targetElementPath;
}

// TODO: test
export function getPathLastInSlot(
  entityState: EntityState<CanvasElement>,
  elementId: CanvasElementId,
  slotId: SlotId,
): ElementTargetPath {
  const element = entityState.entities[elementId];
  if (!hasSlots(element)) {
    throw new Error('Could not find element with slots');
  }

  const slot = element.slots[slotId];
  if (!slot) {
    throw new Error('Could not find slot');
  }

  return {
    id: elementId,
    parentId: elementId,
    slot: slotId,
    index: slot.children.length,
    targetPosition: { type: 'left' },
  };
}
